import { CloudOff, Lock, Trash2 } from "lucide-react";
import { useI18n } from "@/i18n";
import type { Plan } from "@/types";

/**
 * Explains a plan that changes what the panel can show.
 *
 * The free plan is the one that matters: those organisations keep everything
 * on the phones and upload nothing, so every page here would render an empty
 * table. An empty table reads as "your data is gone" — which is exactly the
 * wrong conclusion, and the one most likely to produce a panicked support
 * message. Better to say plainly where the records are.
 */
export function PlanNotice({ plan }: { plan: Plan | undefined }) {
  const { t } = useI18n();

  if (!plan || plan === "premium" || plan === "grace") return null;

  const { Icon, title, body, tone } =
    plan === "readonly"
      ? {
          Icon: Lock,
          title: t("planReadonlyTitle"),
          body: t("planReadonlyBody"),
          tone: "amber" as const,
        }
      : plan === "purged"
        ? {
            Icon: Trash2,
            title: t("planPurgedTitle"),
            body: t("planPurgedBody"),
            tone: "amber" as const,
          }
        : {
            Icon: CloudOff,
            title: t("planFreeTitle"),
            body: t("planFreeBody"),
            tone: "muted" as const,
          };

  return (
    <div
      className={
        tone === "amber"
          ? "mb-4 flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900"
          : "mb-4 flex gap-3 rounded-lg border border-border bg-muted/40 p-4"
      }
    >
      <Icon className="mt-0.5 size-5 shrink-0" />
      <div className="space-y-1">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}
